import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AsyncPipe, Location } from '@angular/common';
import { Component, EventEmitter, inject, input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatInput, MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { ObjetMaquette } from '@app/features/editeur/models/objet-maquette.model';
import { CommandFactoryService } from '@app/features/editeur/services/command-factory.service';
import { EspaceService } from '@app/features/editeur/services/espace.service';
import { ObjetMaquetteService } from '@app/features/editeur/services/objet-maquette.service';
import { AutorisationsService } from '@app/shared/autorisations/autorisations.service';
import { StructureService } from '@app/shared/structure/structure.service';
import {
    CreerFormationRequest,
    CreerGroupementRequest,
    CreerObjetFormationRequest,
    Espace,
    TypeObjetMaquette
} from '@pcscol/odf-api-v1-angular';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, startWith, takeUntil } from 'rxjs/operators';
import { FormMatErrorsDirective } from '../../../../../shared/base-form/form-mat-errors/form-mat-errors.directive';
import { AbstractDescripteursComponent } from '../../../../../shared/form-models/abstract-descripteurs.component';
import { DescripteursObjetMaquetteComponent } from '../editeur-objet-maquette/descripteurs-objet-maquette/descripteurs-objet-maquette.component';
import { CreerObjetMaquetteForm } from './creer-objet-maquette-form';

interface Perimetre {
    code: string;
    libelle: string;
}
export const PERIMETRE_ADMIN = 'PER-ADM';

@Component({
    selector: 'app-creer-objet-maquette',
    templateUrl: './creer-objet-maquette.component.html',
    styleUrls: ['./creer-objet-maquette.component.scss'],
    imports: [
        MatCard,
        MatCardTitle,
        MatCardContent,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        FormMatErrorsDirective,
        MatError,
        MatHint,
        MatSelect,
        MatOption,
        MatTooltip,
        MatCheckbox,
        MatChipGrid,
        MatChipRow,
        MatChipRemove,
        MatIcon,
        MatChipInput,
        MatAutocompleteTrigger,
        MatAutocomplete,
        DescripteursObjetMaquetteComponent,
        MatButton,
        AsyncPipe
    ]
})
export class CreerObjetMaquetteComponent extends AbstractDescripteursComponent implements OnInit, OnDestroy, OnChanges {
    private fb = inject(NonNullableFormBuilder);
    private commandFactoryService = inject(CommandFactoryService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private location = inject(Location);
    private structureService = inject(StructureService);
    private objetMaquetteService = inject(ObjetMaquetteService);
    private espaceService = inject(EspaceService);
    private _autorisationsService = inject(AutorisationsService);
    readonly espace = input.required<Espace>();
    readonly objetMaquette = input<ObjetMaquette>();
    readonly typeObjetMaquette = input<TypeObjetMaquette>();

    readonly TypeObjetMaquette = TypeObjetMaquette;
    readonly parentId = input<string>();
    parent?: ObjetMaquette;
    readonly isUnderEditeur = input<boolean>(false);
    @Output() enfantAjoute = new EventEmitter<string>();
    @Output() annule = new EventEmitter<void>();
    @ViewChild('descripteursObjetMaquetteComponent', { static: false })
    descripteursObjetMaquetteComponent!: DescripteursObjetMaquetteComponent; // Référence de vue pour le FormComponentComponent
    form: CreerObjetMaquetteForm;

    perimetresForm = new FormControl<string>('');
    private destroyed$ = new Subject<void>();
    readonly espaceSelected$ = this.espaceService.espaceSelected$;

    /**
     * pour ces 3 getters on surcharge car le type peut changer
     */
    get isFormation(): boolean {
        return this.form.isFormation;
    }
    get isGroupement(): boolean {
        return this.form.isGroupement;
    }
    get isObjetFormation(): boolean {
        return this.form.isObjetFormation;
    }

    get canBeFormation(): boolean {
        return !this.parent;
    }

    get canBeGroupement(): boolean {
        return true;
    }

    mesPerimetres$: Observable<Perimetre[]>;

    separatorKeysCodes: number[] = [ENTER, COMMA];

    ngOnInit(): void {
        if (this.parentId()) {
            this.loadParent();
        }
        // TODO
        // Pas très beau mais dès angular 16 on pourra faire du @Input() binding depuis le router.
        const espaceId = this.route.snapshot.paramMap.get('espaceId');
        if (!!espaceId && (!this.espaceService.espaceSelected?.id || this.espaceService.espaceSelected?.id !== espaceId)) {
            this.espaceService.selectEspaceById(espaceId);
        }
        this.form = CreerObjetMaquetteForm.createForm(this.fb, this.typeObjetMaquette()!);
        this.mesPerimetres$ = combineLatest([
            this._autorisationsService.mesPerimetres$().pipe(map(ps => ps.filter(p => p.code !== PERIMETRE_ADMIN))),
            this.perimetresForm.valueChanges.pipe(
                filter(val => typeof val === 'string'),
                startWith('')
            ),
            this.form.asFormGroup.controls.perimetres.valueChanges.pipe(startWith([]))
        ]).pipe(
            map(([mesPerimetres, recherche, perimetresSelectionnes]: [Perimetre[], string | null, Perimetre[]]) => {
                if (!recherche) {
                    return mesPerimetres.filter(p => !perimetresSelectionnes.some(ps => ps.code === p.code));
                }
                return mesPerimetres
                    .filter(p => p.code.includes(recherche) || p.libelle.includes(recherche))
                    .filter(p => !perimetresSelectionnes.some(ps => ps.code === p.code));
            })
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.parentId && !changes.parentId.firstChange && !!changes.parentId.currentValue) {
            this.loadParent();
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    formIsValid(): boolean {
        return this.form?.isValid && this.descripteursObjetMaquetteComponent.form.isValid;
    }

    creer() {
        this.commandFactoryService
            .creerObjetMaquetteCommand(this.toCreerRequest())
            .do()
            .pipe(first(), takeUntil(this.destroyed$))
            .subscribe(om => {
                if (this.parent) {
                    this.attacherSousLeParent(om.id!);
                } else {
                    this.router.navigate([this.structureService.current$.getValue(), 'objets-maquette', 'editeur', om.id]);
                }
            });
    }

    annuler() {
        if (this.isUnderEditeur()) {
            this.annule.emit();
        } else {
            this.location.back();
        }
    }

    private attacherSousLeParent(enfantId: string) {
        // TODO obligatoire
        this.commandFactoryService
            .ajouterEnfantCommand(this.parent!.id!, this.parent!.version!, { id: enfantId, obligatoire: true })
            .do()
            .pipe(first(), takeUntil(this.destroyed$))
            .subscribe(() => {
                this.enfantAjoute.emit(this.parent?.id);
                // this.router.navigate([this.structureService.current$.getValue(), 'objets-maquette', 'editeur', this.parentId]);
            });
    }

    private toCreerRequest(): CreerGroupementRequest | CreerObjetFormationRequest | CreerFormationRequest {
        if (this.isFormation) {
            return {
                ...this.form.asPartialRequest,
                espaceId: this.parentId() ? this.parent!.espace! : this.espaceService.espaceSelected!.id!,
                descripteursObjetMaquette: this.descripteursObjetMaquetteComponent.form.asDescripteursObjetMaquette,
                perimetres: this.form.perimetresControl.value.map(p => p.code)
            };
        }
        return {
            ...this.form.asPartialRequest,
            espaceId: this.parentId() ? this.parent!.espace! : this.espaceService.espaceSelected!.id!,
            descripteursObjetMaquette: this.descripteursObjetMaquetteComponent.form.asDescripteursObjetMaquette
        };
    }

    onObjetMaquetteCree() {
        this.resetForm();
    }

    resetForm() {
        this.form.reset();
        this.descripteursObjetMaquetteComponent.reset();
    }

    selected(event: MatAutocompleteSelectedEvent) {
        this.perimetresForm.reset('');
        const perimetresDejaAttaches = this.form.perimetresControl.value;
        this.form.perimetresControl.patchValue([...perimetresDejaAttaches, event.option.value]);
    }

    add(event: MatChipInputEvent) {
        // Clear the input value
        event.chipInput!.clear();
        this.perimetresForm.reset('');
    }

    removePerimetre(perimetreRetire: Perimetre) {
        this.form.perimetresControl.patchValue(this.form.perimetresControl.value.filter(p => p.code !== perimetreRetire.code));
    }

    private loadParent() {
        this.objetMaquetteService
            .lireObjetMaquette(this.parentId()!)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((objetMaquetteRecu: ObjetMaquette) => {
                this.parent = objetMaquetteRecu;
                this.form = CreerObjetMaquetteForm.createForm(this.fb, this.typeObjetMaquette()!);
                this.form.parent = objetMaquetteRecu;
            });
    }
}
